// src/pages/Telephony/AgentGroups.tsx
//
// Ring groups / ACD queues for one organization.
//
// A group is reached by dialling its extension inside the organization's own
// dialplan context, so another tenant cannot reach it — the number does not
// exist in their context, rather than being blocked by a check.
//
// Two settings are separate on purpose and the UI keeps them apart:
//   - STRATEGY distributes a call among whoever is free.
//   - PREFERRED routing runs BEFORE that, expressing "this caller belongs to
//     that agent". No telephony engine's queue can express the second, which is
//     why it is not just another strategy in the dropdown.
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Users, Save, UploadCloud, AlertTriangle } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';

const ALLOWED_ROLES = ['master_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin'];

type Group = {
  id: string;
  name: string;
  extension: string;
  strategy: string;
  preferred_logic: string;
  ring_timeout_seconds: number;
  wrap_up_seconds: number;
  max_wait_seconds: number;
  no_answer_action: string;
  announce_frequency_seconds: number;
  periodic_announce?: string | null;
  periodic_announce_seconds: number;
  music_class?: string | null;
  enabled: boolean;
};

type Seat = { id: string; extension: string; display_name?: string; sip_username: string; enabled: boolean };

// Our names, not the engine's. Asterisk calls round robin "rrmemory" and
// FreeSWITCH calls it "round-robin"; the mapping happens server-side so the
// database never has to describe one engine.
const STRATEGIES = [
  { v: 'ROUND_ROBIN', label: 'Round robin', hint: 'Takes turns, remembering where the last call got to.' },
  { v: 'RING_ALL', label: 'Ring all', hint: 'Rings everyone free at once. Fastest answer, noisiest for agents.' },
  { v: 'FEWEST_CALLS', label: 'Fewest calls', hint: 'Evens out load rather than order.' },
  { v: 'LEAST_RECENT', label: 'Longest idle', hint: 'Whoever has waited longest.' },
  { v: 'LINEAR', label: 'In order', hint: 'Always in member order — the first member takes the most calls.' },
  { v: 'RANDOM', label: 'Random', hint: '' },
];

const AgentGroups: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();
  const allowed = ALLOWED_ROLES.includes(role);
  const isMaster = role === 'master_admin';
  const orgId = currentOrganization?.id;

  const [rows, setRows] = useState<Group[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [members, setMembers] = useState<Record<string, Seat[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newExt, setNewExt] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  // A PIN that has just been issued, held in memory for exactly as long as the
  // admin needs to copy it. Deliberately not persisted anywhere: the server
  // returns it once and has no endpoint to read it back.
  const [pin, setPin] = useState<{ groupId: string; value: string } | null>(null);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true); setError(null);
    try {
      const [g, e] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.GROUPS, { params: { organizationId: orgId } }),
        axiosInstance.get(API_ENDPOINTS.EXTENSIONS, { params: { organizationId: orgId } }),
      ]);
      const groups: Group[] = g.data?.groups ?? [];
      setRows(groups);
      setSeats(e.data?.extensions ?? e.data?.data ?? []);
      // Members are per group, so they are fetched alongside rather than lazily —
      // the count is the first thing anyone looks at, and an empty group is the
      // most common misconfiguration.
      const m: Record<string, Seat[]> = {};
      await Promise.all(groups.map(async (grp) => {
        const r = await axiosInstance.get(API_ENDPOINTS.GROUP_MEMBERS(grp.id), { params: { organizationId: orgId } });
        m[grp.id] = r.data?.members ?? [];
      }));
      setMembers(m);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load groups');
    } finally { setLoading(false); }
  }, [allowed, orgId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError(null); setNotice(null);
    try {
      await axiosInstance.post(API_ENDPOINTS.GROUPS, { name: newName, extension: newExt },
        { params: { organizationId: orgId } });
      setNewName(''); setNewExt('');
      await load();
    } catch (err: any) {
      // The server refuses an extension a seat already uses, and explains why —
      // surface its message rather than a generic one, because the reason
      // (sharing a number makes which one answers depend on dialplan ordering)
      // is not guessable from the UI.
      setError(err.response?.data?.message || 'Could not create the group');
    }
  };

  const patch = async (id: string, body: Partial<Group>) => {
    setError(null);
    try {
      const r = await axiosInstance.put(API_ENDPOINTS.GROUP_BY_ID(id), body, { params: { organizationId: orgId } });
      setNotice(r.data?.note || null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save');
    }
  };

  const setGroupMembers = async (id: string, extensionIds: string[]) => {
    setError(null);
    try {
      const r = await axiosInstance.put(API_ENDPOINTS.GROUP_MEMBERS(id), { extensionIds },
        { params: { organizationId: orgId } });
      setNotice(r.data?.note || null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save members');
    }
  };

  /**
   * Issues a mailbox PIN and shows it once.
   *
   * The server has no GET for this value, so the response is the only time it
   * exists outside the database. It is put on screen and kept out of the URL,
   * the console and localStorage — and the copy says plainly that it will not
   * be shown again, because the previous way to get one was a curl.
   */
  const issuePin = async (groupId: string) => {
    setError(null); setNotice(null);
    try {
      const r = await axiosInstance.post(API_ENDPOINTS.GROUP_VOICEMAIL_PIN(groupId), null,
        { params: { organizationId: orgId } });
      const d = r.data?.data ?? r.data;
      const value = d?.voicemail_pin;
      if (!value) throw new Error('the server issued no PIN');
      setPin({ groupId, value });
      // The PIN reaches Asterisk with the next push, not on save — saying so
      // here avoids someone testing the mailbox immediately and reporting it
      // broken.
      setNotice(d?.note || 'Save this now — it cannot be shown again. Takes effect on the next config push.');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not issue a PIN');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this group? Its membership is deleted too.')) return;
    try {
      await axiosInstance.delete(API_ENDPOINTS.GROUP_BY_ID(id), { params: { organizationId: orgId } });
      await load();
    } catch (err: any) { setError(err.response?.data?.message || 'Could not delete'); }
  };

  if (!allowed) {
    return <div className="p-6 text-sm text-gray-600">You do not have access to group management.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Ring groups</h1>
          <p className="text-sm text-gray-500">
            Calls to a group are offered to its members. Changes reach the switch on the next config push.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="flex gap-2 p-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}
      {notice && <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md">{notice}</div>}

      {/* create */}
      <div className="flex flex-wrap items-end gap-3 p-4 border rounded-md bg-gray-50">
        <label className="text-sm">
          <span className="block mb-1 text-gray-600">Name</span>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Sales"
                 className="px-3 py-2 border rounded-md" />
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-gray-600">Extension</span>
          <input value={newExt} onChange={(e) => setNewExt(e.target.value)} placeholder="2000"
                 className="px-3 py-2 border rounded-md w-28" />
        </label>
        <button onClick={create} disabled={!newName || !newExt}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-40">
          <Plus size={16} /> Add group
        </button>
        <p className="w-full text-xs text-gray-500">
          The extension must not already belong to a seat — a number shared between a seat and a group makes
          which one answers depend on dialplan ordering.
        </p>
      </div>

      {/* list */}
      <div className="space-y-4">
        {rows.length === 0 && !loading && (
          <p className="text-sm text-gray-500">No groups yet.</p>
        )}
        {rows.map((g) => {
          const mem = members[g.id] ?? [];
          const open = editing === g.id;
          return (
            <div key={g.id} className="border rounded-md">
              <div className="flex flex-wrap items-center gap-4 p-4">
                <div className="min-w-40">
                  <div className="font-medium">{g.name}</div>
                  <div className="text-sm text-gray-500">dial {g.extension}</div>
                </div>

                <label className="text-sm">
                  <span className="block mb-1 text-gray-600">Strategy</span>
                  <select value={g.strategy} onChange={(e) => patch(g.id, { strategy: e.target.value })}
                          className="px-2 py-1.5 border rounded-md">
                    {STRATEGIES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </label>

                <label className="text-sm">
                  <span className="block mb-1 text-gray-600">Ring each (s)</span>
                  <input type="number" defaultValue={g.ring_timeout_seconds} min={5}
                         onBlur={(e) => patch(g.id, { ring_timeout_seconds: Number(e.target.value) })}
                         className="w-20 px-2 py-1.5 border rounded-md" />
                </label>

                <label className="text-sm">
                  <span className="block mb-1 text-gray-600">Max wait (s)</span>
                  <input type="number" defaultValue={g.max_wait_seconds} min={10}
                         onBlur={(e) => patch(g.id, { max_wait_seconds: Number(e.target.value) })}
                         className="w-20 px-2 py-1.5 border rounded-md" />
                </label>

                <div className={`text-sm px-2 py-1 rounded ${mem.length ? 'bg-gray-100 text-gray-700' : 'bg-red-50 text-red-700'}`}>
                  <Users size={14} className="inline mr-1" />
                  {mem.length} member{mem.length === 1 ? '' : 's'}
                  {mem.length === 0 && ' — callers are refused'}
                </div>

                <div className="ml-auto flex items-center gap-2">
                  {/* Only offered when the group actually sends callers to a
                      mailbox — a PIN for a group that never takes voicemail is
                      a credential with nothing behind it. */}
                  {g.no_answer_action === 'VOICEMAIL' && (
                    <button onClick={() => issuePin(g.id)}
                            className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50">
                      Issue voicemail PIN
                    </button>
                  )}
                  <button onClick={() => setEditing(open ? null : g.id)}
                          className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50">
                    {open ? 'Close' : 'Members'}
                  </button>
                  <button onClick={() => remove(g.id)} className="p-2 text-red-600 rounded-md hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {pin?.groupId === g.id && (
                <div className="mx-4 mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
                  <div className="text-xs text-amber-800 dark:text-amber-200">
                    Mailbox PIN for <span className="font-medium">{g.name}</span> — shown once.
                  </div>
                  <div className="mt-1 flex items-center gap-3">
                    <code className="font-mono text-lg tracking-widest text-amber-900 dark:text-amber-100">{pin.value}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(pin.value); }}
                            className="px-2 py-1 text-xs border border-amber-300 rounded hover:bg-amber-100 dark:border-amber-700">
                      Copy
                    </button>
                    <button onClick={() => setPin(null)}
                            className="px-2 py-1 text-xs text-amber-700 hover:underline dark:text-amber-300">
                      I have saved it
                    </button>
                  </div>
                </div>
              )}

              {open && (
                <div className="px-4 pb-4 border-t pt-4">
                  <p className="mb-2 text-xs text-gray-500">
                    Order matters: calls are offered in this order for “In order”, and it sets the fallback tiers
                    for the others. A seat that is also the caller will be rung by its own group — avoid putting a
                    test extension in a group it dials.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {seats.map((s) => {
                      const on = mem.some((m) => m.id === s.id);
                      return (
                        <button key={s.id}
                                onClick={() => setGroupMembers(g.id,
                                  on ? mem.filter((m) => m.id !== s.id).map((m) => m.id)
                                     : [...mem.map((m) => m.id), s.id])}
                                className={`px-3 py-1.5 text-sm border rounded-md ${on ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-50'}`}>
                          {s.extension}{s.display_name ? ` · ${s.display_name}` : ''}
                        </button>
                      );
                    })}
                    {seats.length === 0 && <span className="text-sm text-gray-500">No seats in this organization.</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isMaster && <ConfigPush onError={setError} onNotice={setNotice} />}
    </div>
  );
};

/**
 * Preview and push, master admin only.
 *
 * <p>The push writes config for the WHOLE estate to one node, not just this
 * organization — the node holds one file per kind, so a partial push would
 * delete every other tenant's config. That is why this is not a per-group
 * action, and why the preview shows everything.
 */
const ConfigPush: React.FC<{ onError: (s: string) => void; onNotice: (s: string) => void }> = ({ onError, onNotice }) => {
  const [preview, setPreview] = useState<any>(null);
  const [providerId, setProviderId] = useState('1');
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.get(API_ENDPOINTS.EXTENSION_CONFIG_PREVIEW);
      setPreview(r.data);
    } catch (e: any) { onError(e.response?.data?.message || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const doPush = async () => {
    if (!window.confirm('Push config to this node? It REPLACES the seat and dialplan files for every organization.')) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(`${API_ENDPOINTS.EXTENSION_CONFIG_PUSH}?providerId=${providerId}`);
      // The server reports each half separately, and says when queue definitions
      // still need placing by hand — surface that rather than a bare "done".
      const note = r.data?.applied === false
        ? 'The node did NOT apply everything — see the sections below.'
        : (r.data?.queuesNote || 'Applied.');
      onNotice(String(note));
      setPreview(r.data);
    } catch (e: any) { onError(e.response?.data?.message || 'Push failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 border rounded-md">
      <h2 className="font-medium">Switch configuration</h2>
      <p className="mt-1 mb-3 text-sm text-gray-500">
        Groups and seats only take effect once pushed. The push replaces the files for every organization on the
        chosen node, so preview first.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block mb-1 text-gray-600">Node (provider id)</span>
          <input value={providerId} onChange={(e) => setProviderId(e.target.value)} className="w-24 px-2 py-1.5 border rounded-md" />
        </label>
        <button onClick={doPreview} disabled={busy} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-40">
          Preview
        </button>
        <button onClick={doPush} disabled={busy}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-40">
          <UploadCloud size={16} /> Push
        </button>
      </div>

      {preview && (
        <div className="mt-4 space-y-3">
          <div className="text-sm text-gray-600">
            {preview.seatCount ?? 0} seat(s), {preview.groupCount ?? 0} group(s)
            {preview.organizations ? ` across ${preview.organizations.length} organization(s)` : ''}
          </div>
          {preview.queuesNote && (
            <div className="flex gap-2 p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{preview.queuesNote}</span>
            </div>
          )}
          {/* Preview returns each file as a STRING; push returns a RESULT OBJECT per
              half ({success, message, ...}). Rendering an object as a React child
              throws and unmounts the whole tree — which is exactly what happened:
              the push worked well enough to answer, and the page went blank before
              it could show what the node said. Never render a raw value. */}
          {['dialplan', 'queues', 'pjsip'].map((k) => {
            const v = preview[k];
            if (v === undefined || v === null) return null;
            const text = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
            const ok = typeof v === 'object' ? v.success : undefined;
            return (
              <details key={k} className="border rounded-md">
                <summary className="px-3 py-2 text-sm cursor-pointer bg-gray-50">
                  {k}
                  {ok === true && <span className="ml-2 text-green-700">applied</span>}
                  {ok === false && <span className="ml-2 text-red-700">FAILED — {String(v.message ?? '')}</span>}
                </summary>
                <pre className="p-3 overflow-auto text-xs max-h-80">{text}</pre>
              </details>
            );
          })}
          {preview.applied === false && (
            <div className="flex gap-2 p-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>The node did not apply everything. Open each section above for the reason it gave.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentGroups;
